import { getAnalyticsConsent } from "$lib/analytics/consent"

export const ATTRIBUTION_COOKIE_NAME = "pa_ad_attribution"
export const ATTRIBUTION_STORAGE_KEY = "promoamp:ad-attribution:v1"

const ATTRIBUTION_MAX_AGE_SECONDS = 60 * 60 * 24 * 90
const SESSION_TIMEOUT_MS = 30 * 60 * 1000
const MAX_VALUE_LENGTH = 180

export const ADVERTISING_EVENT_NAMES = [
  "landing_view",
  "pricing_viewed",
  "signup_started",
  "account_created",
  "account_identified",
  "onboarding_completed",
  "campaign_created",
  "campaign_published",
  "checkout_started",
  "trial_started",
  "subscription_paid",
] as const

export type AdvertisingEventName = (typeof ADVERTISING_EVENT_NAMES)[number]

export interface AttributionTouch {
  source: string
  medium: string
  campaign?: string
  campaignId?: string
  content?: string
  term?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  fbclid?: string
  msclkid?: string
  ttclid?: string
  liFatId?: string
  landingPath: string
  referrerHost?: string
  capturedAt: string
}

export interface AdvertisingAttribution {
  version: 1
  visitorId: string
  sessionId: string
  sessionStartedAt: string
  lastSeenAt: string
  firstTouch: AttributionTouch
  lastTouch: AttributionTouch
}

type Primitive = string | number | boolean | null

const queryParameterMap = {
  campaign: "utm_campaign",
  campaignId: "utm_id",
  content: "utm_content",
  term: "utm_term",
  gclid: "gclid",
  gbraid: "gbraid",
  wbraid: "wbraid",
  fbclid: "fbclid",
  msclkid: "msclkid",
  ttclid: "ttclid",
  liFatId: "li_fat_id",
} as const

const clickSources = [
  { parameter: "gclid", source: "google" },
  { parameter: "gbraid", source: "google" },
  { parameter: "wbraid", source: "google" },
  { parameter: "fbclid", source: "facebook" },
  { parameter: "msclkid", source: "bing" },
  { parameter: "ttclid", source: "tiktok" },
  { parameter: "li_fat_id", source: "linkedin" },
] as const

function cleanValue(value: unknown, maxLength = MAX_VALUE_LENGTH) {
  if (typeof value !== "string") return undefined
  const cleaned = [...value.trim()]
    .filter((character) => {
      const codePoint = character.codePointAt(0) || 0
      return codePoint > 31 && codePoint !== 127
    })
    .join("")
  return cleaned ? cleaned.slice(0, maxLength) : undefined
}

function normalizeHost(hostname: string) {
  return hostname.toLowerCase().replace(/^www\./, "")
}

function getExternalReferrerHost(referrer: string, destination: URL) {
  if (!referrer) return undefined

  try {
    const referrerUrl = new URL(referrer)
    const referrerHost = normalizeHost(referrerUrl.hostname)
    return referrerHost === normalizeHost(destination.hostname)
      ? undefined
      : cleanValue(referrerHost)
  } catch {
    return undefined
  }
}

function isSearchEngine(hostname: string) {
  return [
    "google.",
    "bing.com",
    "search.yahoo.",
    "duckduckgo.com",
    "ecosia.org",
    "baidu.com",
    "yandex.",
  ].some((searchHost) => hostname.includes(searchHost))
}

function searchSource(hostname: string) {
  if (hostname.includes("google.")) return "google"
  if (hostname.includes("bing.com")) return "bing"
  if (hostname.includes("yahoo.")) return "yahoo"
  if (hostname.includes("duckduckgo.com")) return "duckduckgo"
  if (hostname.includes("ecosia.org")) return "ecosia"
  if (hostname.includes("baidu.com")) return "baidu"
  if (hostname.includes("yandex.")) return "yandex"
  return hostname
}

function hasCampaignSignal(url: URL) {
  return (
    url.searchParams.has("utm_source") ||
    url.searchParams.has("utm_medium") ||
    url.searchParams.has("utm_campaign") ||
    clickSources.some(({ parameter }) => url.searchParams.has(parameter))
  )
}

export function createAttributionTouch(
  url: URL,
  referrer = "",
  capturedAt = new Date(),
) {
  const externalReferrerHost = getExternalReferrerHost(referrer, url)
  const explicitSource = cleanValue(url.searchParams.get("utm_source"))
  const explicitMedium = cleanValue(url.searchParams.get("utm_medium"))
  const clickSource = clickSources.find(({ parameter }) =>
    url.searchParams.has(parameter),
  )?.source

  let source = explicitSource
  let medium = explicitMedium

  if (!source && clickSource) source = clickSource
  if (!medium && clickSource) medium = "paid"

  if (!source && externalReferrerHost) {
    source = isSearchEngine(externalReferrerHost)
      ? searchSource(externalReferrerHost)
      : externalReferrerHost
    medium = isSearchEngine(externalReferrerHost) ? "organic" : "referral"
  }

  source ||= "(direct)"
  medium ||= source === "(direct)" ? "(none)" : "unknown"

  const touch: AttributionTouch = {
    source,
    medium,
    landingPath: cleanValue(url.pathname, 500) || "/",
    capturedAt: capturedAt.toISOString(),
  }

  if (externalReferrerHost) touch.referrerHost = externalReferrerHost

  for (const [field, parameter] of Object.entries(queryParameterMap)) {
    const value = cleanValue(url.searchParams.get(parameter))
    if (value) {
      ;(touch as unknown as Record<string, string>)[field] = value
    }
  }

  return {
    touch,
    attributable:
      hasCampaignSignal(url) ||
      Boolean(externalReferrerHost) ||
      Boolean(explicitSource),
  }
}

function createId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (token) => {
    const random = Math.floor(Math.random() * 16)
    const value = token === "x" ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

export function mergeAttribution(
  previous: AdvertisingAttribution | null,
  url: URL,
  referrer = "",
  now = new Date(),
) {
  const { touch, attributable } = createAttributionTouch(url, referrer, now)
  const previousLastSeen = previous
    ? Date.parse(previous.lastSeenAt)
    : Number.NaN
  const startsNewSession =
    !previous ||
    !Number.isFinite(previousLastSeen) ||
    now.getTime() - previousLastSeen > SESSION_TIMEOUT_MS

  if (!previous) {
    return {
      version: 1,
      visitorId: createId(),
      sessionId: createId(),
      sessionStartedAt: now.toISOString(),
      lastSeenAt: now.toISOString(),
      firstTouch: touch,
      lastTouch: touch,
    } satisfies AdvertisingAttribution
  }

  return {
    ...previous,
    sessionId: startsNewSession ? createId() : previous.sessionId,
    sessionStartedAt: startsNewSession
      ? now.toISOString()
      : previous.sessionStartedAt,
    lastSeenAt: now.toISOString(),
    // A direct/internal revisit must not erase the last meaningful campaign.
    lastTouch: attributable ? touch : previous.lastTouch,
  } satisfies AdvertisingAttribution
}

function isTouch(value: unknown): value is AttributionTouch {
  if (!value || typeof value !== "object") return false
  const touch = value as Partial<AttributionTouch>
  const optionalValues = [
    touch.campaign,
    touch.campaignId,
    touch.content,
    touch.term,
    touch.gclid,
    touch.gbraid,
    touch.wbraid,
    touch.fbclid,
    touch.msclkid,
    touch.ttclid,
    touch.liFatId,
    touch.referrerHost,
  ]
  return (
    typeof touch.source === "string" &&
    touch.source.length > 0 &&
    touch.source.length <= MAX_VALUE_LENGTH &&
    typeof touch.medium === "string" &&
    touch.medium.length > 0 &&
    touch.medium.length <= MAX_VALUE_LENGTH &&
    typeof touch.landingPath === "string" &&
    touch.landingPath.length > 0 &&
    touch.landingPath.length <= 500 &&
    typeof touch.capturedAt === "string" &&
    Number.isFinite(Date.parse(touch.capturedAt)) &&
    optionalValues.every(
      (item) =>
        item === undefined ||
        (typeof item === "string" && item.length <= MAX_VALUE_LENGTH),
    )
  )
}

export function parseAdvertisingAttribution(
  value: unknown,
): AdvertisingAttribution | null {
  if (!value || typeof value !== "object") return null
  const attribution = value as Partial<AdvertisingAttribution>

  if (
    attribution.version !== 1 ||
    typeof attribution.visitorId !== "string" ||
    attribution.visitorId.length < 8 ||
    attribution.visitorId.length > 128 ||
    !/^[A-Za-z0-9_-]+$/.test(attribution.visitorId) ||
    typeof attribution.sessionId !== "string" ||
    attribution.sessionId.length < 8 ||
    attribution.sessionId.length > 128 ||
    typeof attribution.sessionStartedAt !== "string" ||
    !Number.isFinite(Date.parse(attribution.sessionStartedAt)) ||
    typeof attribution.lastSeenAt !== "string" ||
    !Number.isFinite(Date.parse(attribution.lastSeenAt)) ||
    !isTouch(attribution.firstTouch) ||
    !isTouch(attribution.lastTouch)
  ) {
    return null
  }

  return attribution as AdvertisingAttribution
}

export function parseAttributionCookie(value: string | undefined) {
  if (!value) return null

  for (const candidate of [value, safeDecodeURIComponent(value)]) {
    try {
      const parsed = parseAdvertisingAttribution(JSON.parse(candidate))
      if (parsed) return parsed
    } catch {
      // Try the next decoding form.
    }
  }

  return null
}

function safeDecodeURIComponent(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function trackingIsSuppressed() {
  if (typeof window === "undefined") return true
  return getAnalyticsConsent() !== "granted"
}

function clearStoredAttribution() {
  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
  } catch {
    // The cookie can still be expired.
  }

  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${ATTRIBUTION_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax${secure}`
}

function readStoredAttribution() {
  try {
    const localValue = localStorage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (localValue) {
      const parsed = parseAdvertisingAttribution(JSON.parse(localValue))
      if (parsed) return parsed
    }
  } catch {
    // Storage can be unavailable in strict privacy modes.
  }

  const cookieValue = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${ATTRIBUTION_COOKIE_NAME}=`))
    ?.slice(ATTRIBUTION_COOKIE_NAME.length + 1)

  return parseAttributionCookie(cookieValue)
}

function persistAttribution(attribution: AdvertisingAttribution) {
  const serialized = JSON.stringify(attribution)

  try {
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, serialized)
  } catch {
    // The first-party cookie remains as a fallback.
  }

  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(serialized)}; Path=/; Max-Age=${ATTRIBUTION_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
}

export function initializeAdvertisingAttribution() {
  if (trackingIsSuppressed()) {
    clearStoredAttribution()
    return null
  }

  const attribution = mergeAttribution(
    readStoredAttribution(),
    new URL(window.location.href),
    document.referrer,
  )
  persistAttribution(attribution)
  return attribution
}

export function sanitizeEventProperties(
  properties: Record<string, unknown> = {},
) {
  const sanitized: Record<string, Primitive> = {}

  for (const [rawKey, rawValue] of Object.entries(properties).slice(0, 25)) {
    const key = cleanValue(rawKey, 64)
    if (!key || ["__proto__", "constructor", "prototype"].includes(key)) {
      continue
    }

    if (typeof rawValue === "string") {
      sanitized[key] = cleanValue(rawValue, MAX_VALUE_LENGTH) || ""
    } else if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
      sanitized[key] = rawValue
    } else if (typeof rawValue === "boolean" || rawValue === null) {
      sanitized[key] = rawValue
    }
  }

  return sanitized
}

function sessionEventWasSent(key: string) {
  try {
    return sessionStorage.getItem(key) === "1"
  } catch {
    return false
  }
}

function markSessionEventSent(key: string) {
  try {
    sessionStorage.setItem(key, "1")
  } catch {
    // In-memory delivery still works without session storage.
  }
}

function isPromoAmpHost(hostname: string) {
  return (
    hostname === "promoamp.com" ||
    hostname === "www.promoamp.com" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1"
  )
}

function isMarketingPath(pathname: string) {
  const excludedPrefixes = [
    "/account",
    "/api",
    "/auth",
    "/brand",
    "/c",
    "/checkout",
    "/connect",
    "/dev",
    "/embed",
    "/invite",
    "/onboarding",
    "/preview",
    "/sign-in",
    "/sign-up",
    "/sso",
    "/webhooks",
  ]

  return !excludedPrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
}

export async function trackAdvertisingEvent(
  eventName: AdvertisingEventName,
  properties: Record<string, unknown> = {},
  options: { emitToDestinations?: boolean; eventId?: string } = {},
) {
  const attribution = initializeAdvertisingAttribution()
  if (!attribution) return false

  const eventId = options.eventId || createId()
  const cleanProperties = sanitizeEventProperties(properties)
  const path = window.location.pathname.slice(0, 500)

  if (options.emitToDestinations !== false) {
    window.dataLayer ||= []
    window.dataLayer.push({
      event: `promoamp_${eventName}`,
      promoamp: {
        eventId,
        eventName,
        source: attribution.lastTouch.source,
        medium: attribution.lastTouch.medium,
        campaign: attribution.lastTouch.campaign,
        properties: cleanProperties,
      },
    })

    window.oa?.track(eventName, {
      source: attribution.lastTouch.source,
      medium: attribution.lastTouch.medium,
      campaign: attribution.lastTouch.campaign || "(not set)",
      ...cleanProperties,
    })

    try {
      window.bento?.track(eventName, cleanProperties)
    } catch {
      // Ad attribution must not interrupt the product journey.
    }
  }

  try {
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), 1500)
    const response = await fetch("/api/analytics/events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventId,
        eventName,
        visitorId: attribution.visitorId,
        sessionId: attribution.sessionId,
        path,
        properties: cleanProperties,
        attribution,
        occurredAt: new Date().toISOString(),
      }),
      signal: controller.signal,
    })
    window.clearTimeout(timeout)

    return response.ok
  } catch {
    return false
  }
}

export function trackPromoAmpRoute(pathname: string) {
  const attribution = initializeAdvertisingAttribution()
  if (
    !attribution ||
    !isPromoAmpHost(window.location.hostname) ||
    !isMarketingPath(pathname)
  ) {
    return
  }

  const landingKey = `promoamp:landing:${attribution.sessionId}`
  if (!sessionEventWasSent(landingKey)) {
    markSessionEventSent(landingKey)
    void trackAdvertisingEvent("landing_view", {
      landing_path: pathname,
    })
  }

  if (pathname === "/pricing") {
    const pricingKey = `promoamp:pricing:${attribution.sessionId}`
    if (!sessionEventWasSent(pricingKey)) {
      markSessionEventSent(pricingKey)
      void trackAdvertisingEvent("pricing_viewed")
    }
  }
}

export async function identifyAdvertisingVisitor(userId: string) {
  const attribution = initializeAdvertisingAttribution()
  if (!attribution) return false

  const key = `promoamp:identified:${attribution.sessionId}:${userId}`
  if (sessionEventWasSent(key)) return true

  const linked = await trackAdvertisingEvent(
    "account_identified",
    {},
    { emitToDestinations: false },
  )
  if (linked) markSessionEventSent(key)
  return linked
}
